import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface CarouselScrollerProps {
  children: ReactNode;
  className?: string;
}

export function CarouselScroller({ children, className }: CarouselScrollerProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const scrollable = el.scrollWidth - el.clientWidth > 4;
    setCanLeft(scrollable && el.scrollLeft > 4);
    setCanRight(scrollable && el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    update();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    Array.from(el.children).forEach((c) => ro.observe(c));
    const t = window.setTimeout(update, 600);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      ro.disconnect();
      window.clearTimeout(t);
    };
  }, [children]);

  const scrollBy = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(260, el.clientWidth * 0.8), behavior: "smooth" });
  };

  return (
    <div className="relative group/carousel">
      <div
        ref={scrollerRef}
        className={cn(
          "max-w-full flex gap-3 overflow-x-auto overscroll-x-contain snap-x snap-mandatory scrollbar-hide",
          className,
        )}
      >
        {children}
      </div>
      {/* Desktop arrows — always visible, dimmed when not scrollable in that direction */}
      <button
        type="button"
        aria-label="Scroll left"
        onClick={() => scrollBy(-1)}
        disabled={!canLeft}
        className={cn(
          "hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white shadow-lg hover:bg-black/80 transition-opacity",
          canLeft ? "opacity-100" : "opacity-40 cursor-not-allowed",
        )}
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Scroll right"
        onClick={() => scrollBy(1)}
        disabled={!canRight}
        className={cn(
          "hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white shadow-lg hover:bg-black/80 transition-opacity",
          canRight ? "opacity-100" : "opacity-40 cursor-not-allowed",
        )}
      >
        <ChevronRight className="h-5 w-5" />
      </button>

    </div>
  );
}
